import { MarketImpactType } from '@appTypes/MarketImpact';
import { RoutePaths } from '@enums/RoutePaths';
import { useUserDealer } from '@hooks/UseUserDealer';
import { useLocation, useNavigate } from 'react-router';

export const useRouterService = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isIncentiveAdvantageType } = useUserDealer();

  const goToMarketConquestAndOpenCustomerProfileModal = (id: string, dealerId: string) => {
    const currentUrl = location.pathname;

    if (currentUrl.includes('acquire')) {
      const event = new CustomEvent('market-conquest-open-customer-profile', {
        detail: JSON.stringify({ id, dealerId }),
      });
      document.dispatchEvent(event);
      return;
    }

    navigate(`${RoutePaths.Acquire}/${id}/${dealerId}`);
  };

  const goToPortfolio = (dealType: string) => {
    const portfolioNav = { state: 'app.portfolio', route: dealType };
    localStorage.setItem('lastNavigation', JSON.stringify(portfolioNav));

    navigate(`/portfolio/${dealType}`);
  };

  const goToIADealsheet = (closedDealId: string, dealerId: string) => {
    navigate(`${RoutePaths.EssentialDealsheet}?closedDealId=${closedDealId}/dealerId=${dealerId}`);
  };

  const goToDealsheet = (id: string, dealerId: string, dealType: string) => {
    const isMobileDevice = (): boolean => {
      const windowWidth = window.innerWidth;
      return windowWidth > 0 && windowWidth <= 752;
    };

    const path = isMobileDevice()
      ? `/dealsheet/${id}/${dealerId}/offer/1/${dealType}/m`
      : `/dealsheet/${id}/${dealerId}/offer/1/${dealType}/bps`;

    navigate(path, { replace: true });
  };

  const goToHome = () => {
    navigate(RoutePaths.Home);
  };

  const goToServiceConquest = () => {
    navigate(RoutePaths.Service);
  };

  const goToEUIDashboard = () => {
    navigate(RoutePaths.EssentialDashboard);
  };

  const goToAdminPanel = (isActive: boolean) => {
    if (isActive) {
      navigate(RoutePaths.AdminDashboard);
    }
  };

  const goToReporting = (reportName: string) => {
    const currentUrl = location.pathname;

    if (currentUrl.indexOf(reportName) <= -1) {
      if (reportName === 'performanceInsights') {
        navigate(RoutePaths.PerformanceInsights);
      } else if (reportName === 'privateIncentiveInsights') {
        navigate(RoutePaths.PrivateIncentiveInSights);
      } else if (reportName === 'service-portfolio') {
        navigate(RoutePaths.ServicePortfolio);
      } else {
        if (isIncentiveAdvantageType) {
          navigate(RoutePaths.EssentialDashboard);
        } else {
          navigate(RoutePaths.PortfolioLease);
        }
      }
    }
  };

  const goToAcquire = () => {
    navigate(RoutePaths.Acquire);
  };

  const goToDigitalAudience = () => {
    navigate(RoutePaths.DigitalAudienceDashboard);
  };

  const goToPrivateIncentiveLookup = () => {
    navigate(RoutePaths.PrivateIncentiveLookup);
  };

  const goToMarketImpact = (type: MarketImpactType) => {
    navigate(`/market-impact/${type}`);
  };

  const goToTurn = () => {
    const turnDashboardNav = { state: 'app.turn.dashboard', route: `/turn` };
    localStorage.setItem('lastNavigation', JSON.stringify(turnDashboardNav));

    navigate(RoutePaths.Turn);
  };

  const goToMarketImpactSummary = () => {
    navigate(RoutePaths.MarketImpactSummary);
  };

  const goToRecent = () => {
    navigate(RoutePaths.Recent);
  };

  const goToFavorites = () => {
    navigate(RoutePaths.Favorites);
  };

  const goToYourProfile = () => {
    navigate(RoutePaths.YourProfile);
  };

  const goToSupport = () => {
    navigate(RoutePaths.Support);
  };

  const goToPwa = (isInitial?: boolean) => {
    if (!isInitial) {
      navigate(`${RoutePaths.PWA}?initialLoad=${isInitial}`);
    } else {
      navigate(RoutePaths.PWA);
    }
  };

  const goToGetMobileApp = (showHeaderWithLogout?: boolean) => {
    if (showHeaderWithLogout) {
      navigate(`${RoutePaths.GetMobileApp}?initialLoad=${showHeaderWithLogout}`);
    } else {
      navigate(RoutePaths.GetMobileApp);
    }
  };

  const goToBulkPrint = async (): Promise<boolean> => {
    return new Promise((res) => {
      window.setTimeout(() => {
        const newWin = window.open(RoutePaths.BulkPrint, '_blank');
        if (newWin) {
          newWin.addEventListener('load', () => {
            newWin.document.title = 'Bulk Print Offer - Mastermind';
          });
        }
        if (!newWin || newWin.closed || typeof newWin.closed == 'undefined') {
          // POPUP BLOCKED
          res(true);
        } else {
          res(false);
        }
      }, 5000);
    });
  };

  return {
    goToBulkPrint,
    goToHome,
    goToServiceConquest,
    goToMarketConquestAndOpenCustomerProfileModal,
    goToPortfolio,
    goToDealsheet,
    goToIADealsheet,
    goToEUIDashboard,
    goToAdminPanel,
    goToReporting,
    goToAcquire,
    goToDigitalAudience,
    goToPrivateIncentiveLookup,
    goToMarketImpact,
    goToTurn,
    goToMarketImpactSummary,
    goToRecent,
    goToFavorites,
    goToYourProfile,
    goToSupport,
    goToPwa,
    goToGetMobileApp,
  };
};
